/**
 * Date/Time formatting utility for IST (Indian Standard Time).
 * 
 * All dates from the backend are in UTC.
 * All dates displayed to users should be in IST (DD-MM-YYYY, hh:mm AM/PM IST).
 * 
 * IST = UTC + 5 hours 30 minutes
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds

/**
 * Convert a UTC date to IST Date object.
 * @param {Date|string|number} date - The date to convert
 * @returns {Date} - A new Date object adjusted to IST
 */
const toIST = (date) => {
  const d = new Date(date);
  return new Date(d.getTime() + IST_OFFSET_MS);
};

/**
 * Format a date as "DD-MM-YYYY, hh:mm AM/PM IST"
 * @param {Date|string|number} date - The date to format
 * @returns {string} - Formatted date string in IST
 */
export const formatDateIST = (date) => {
  if (!date) return 'N/A';

  const ist = toIST(date);

  const day = String(ist.getUTCDate()).padStart(2, '0');
  const month = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const year = ist.getUTCFullYear();

  let hours = ist.getUTCHours();
  const minutes = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const hoursStr = String(hours).padStart(2, '0');

  return `${day}-${month}-${year}, ${hoursStr}:${minutes} ${ampm} IST`;
};

/**
 * Format a date as "DD-MM-YYYY" (date only, no time)
 * @param {Date|string|number} date - The date to format
 * @returns {string} - Formatted date string in IST
 */
export const formatDateOnlyIST = (date) => {
  if (!date) return 'N/A';

  const ist = toIST(date);

  const day = String(ist.getUTCDate()).padStart(2, '0');
  const month = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const year = ist.getUTCFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Format a date as "DD Mon YYYY" (e.g., "15 June 2025")
 * Used for formal displays.
 * @param {Date|string|number} date - The date to format
 * @returns {string} - Formatted date string in IST
 */
export const formatDateLongIST = (date) => {
  if (!date) return 'N/A';

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const ist = toIST(date);

  const day = ist.getUTCDate();
  const month = months[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();

  return `${day} ${month} ${year}`;
};

/**
 * Format a date as "DD Mon YYYY" short month (e.g., "15 Jun 2025")
 * @param {Date|string|number} date - The date to format
 * @returns {string} - Formatted date string in IST
 */
export const formatDateShortIST = (date) => {
  if (!date) return 'N/A';

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const ist = toIST(date);

  const day = ist.getUTCDate();
  const month = months[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();

  return `${day} ${month} ${year}`;
};
